import { useState } from "react";
import { toast } from "sonner";
import { Eye, Send, SkipForward } from "lucide-react";
import { api, errMsg, inr } from "@/lib/api";
import { Panel } from "@/components/admin/AdminLayout";

// Telegram's own formatting tags only; values are already escaped by the server.
const toHtml = (caption) => caption.replace(/\n/g, "<br/>");

const Preview = ({ p }) => (
  <div className="max-w-md rounded-2xl bg-sky-50 border border-sky-200 p-3" data-testid="tg-golive-preview">
    {p.image_url && <img src={p.image_url} alt="" className="rounded-lg mb-2 w-full" />}
    <div className="text-sm text-slate-800 [&_code]:bg-white [&_code]:px-1 [&_code]:rounded" data-testid="tg-golive-caption" dangerouslySetInnerHTML={{ __html: toHtml(p.caption) }} />
    <div className="mt-3 text-center text-sm font-bold text-sky-700 bg-white rounded-lg py-2 border border-sky-200">{p.button}</div>
    <p className="text-[11px] text-slate-500 mt-2 break-all" data-testid="tg-golive-link">Button opens: {p.link}</p>
  </div>
);

export const TelegramGoLive = () => {
  const [p, setP] = useState(null);
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e), { duration: 10000 }); } finally { setBusy(false); } };
  const load = (n) => run(async () => { setSkip(n); setP((await api.post("/admin/telegram/test-deal/preview", { skip: n })).data); });
  const send = () => run(async () => {
    if (!window.confirm(`Send this ${p.kind} to your Telegram channel now? Subscribers will see it.`)) return;
    await api.post("/admin/telegram/test-deal/send", p.kind === "deal" ? { deal_id: p.item.id } : { coupon_id: p.item.id });
    toast.success("Posted - check your channel"); setP({ ...p, already_posted: true });
  });
  return (
    <Panel title="First deal post - preview, then you approve" testid="tg-golive">
      <p className="text-sm text-slate-600 mb-3">Nothing is sent until you press <b>Send this test</b>. Automatic posting stays off until you switch it on in Settings → Social posting.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => load(0)} disabled={busy} className="px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 border border-slate-300 bg-white disabled:opacity-40" data-testid="tg-golive-preview-button"><Eye size={15} /> Preview first post</button>
        {p && <button onClick={() => load(skip + 1)} disabled={busy} className="px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 border border-slate-300 bg-white disabled:opacity-40" data-testid="tg-golive-next-button"><SkipForward size={15} /> Show another</button>}
        {p && <button onClick={send} disabled={busy || p.already_posted || !p.can_post} className="px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 bg-sky-600 text-white disabled:opacity-40" data-testid="tg-golive-send-button"><Send size={15} /> Send this test</button>}
      </div>
      {p && !p.can_post && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-testid="tg-golive-blocker">Sending is locked until the bot can post. {p.blocker}</p>}
      {p && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500" data-testid="tg-golive-item">
            {p.kind === "deal" ? `Deal · ${p.item.merchant_name} · ${inr(p.item.price)} · score ${p.item.score}` : `Coupon · ${p.item.merchant_name}${p.item.code ? ` · code ${p.item.code}` : ""}`}
            {p.already_posted && <b className="text-amber-700"> · already posted</b>}
          </p>
          <Preview p={p} />
        </div>
      )}
    </Panel>
  );
};
